import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiEye, FiChevronDown, FiChevronUp, FiMove } from "react-icons/fi";
import {
  exitImpersonation,
  getImpersonatedUser,
  isImpersonating,
} from "../../../helpers/impersonation";
import { useNavigate } from 'react-router-dom';

const MARGIN = 16;

const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  // Clamp a point so the widget's full (measured) footprint stays on screen.
  const clamp = (x: number, y: number) => {
    const w = containerRef.current?.offsetWidth ?? 0;
    const h = containerRef.current?.offsetHeight ?? 0;
    const maxX = Math.max(MARGIN, window.innerWidth - w - MARGIN);
    const maxY = Math.max(MARGIN, window.innerHeight - h - MARGIN);
    return {
      x: Math.min(Math.max(MARGIN, x), maxX),
      y: Math.min(Math.max(MARGIN, y), maxY),
    };
  };

  useLayoutEffect(() => {
    setPos((prev) => (prev ? clamp(prev.x, prev.y) : prev));
  }, [expanded]);

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      movedRef.current = true;
      setPos(
        clamp(e.clientX - offsetRef.current.x, e.clientY - offsetRef.current.y)
      );
    };

    const handleUp = () => {
      draggingRef.current = false;
    };

    // Keep the widget on screen if the viewport is resized.
    const handleResize = () => {
      setPos((prev) => (prev ? clamp(prev.x, prev.y) : prev));
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!isImpersonating()) return null;

  const user = getImpersonatedUser();
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "this user";

  const startDrag = (e: React.PointerEvent) => {
    draggingRef.current = true;
    movedRef.current = false;
    offsetRef.current = {
      x: e.clientX - (pos?.x ?? 0),
      y: e.clientY - (pos?.y ?? 0),
    };
  };

  const toggleExpand = () => {
    // Ignore the click that ends a drag so dragging never toggles the panel.
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    setExpanded((prev) => !prev);
  };

  return (
    <div
      className="fixed z-[1000]"
      style={
        pos ? { left: pos.x, top: pos.y } : { right: MARGIN, bottom: MARGIN }
      }
    >
      <div
        ref={containerRef}
        className="flex items-center gap-2 rounded-full border border-[#356C58]/50 bg-[#111827] px-2.5 py-2 text-sm text-white shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
      >
        {/* Drag handle */}
        <button
          type="button"
          onPointerDown={startDrag}
          aria-label="Drag"
          className="flex h-7 w-7 shrink-0 cursor-grab items-center justify-center rounded-full  text-white transition  active:cursor-grabbing"
        >
          <FiMove size={14} />
        </button>

        {/* Eye icon with live green dot */}
        <span className="relative flex shrink-0 items-center justify-center text-[#77b991]">
          <FiEye />
          <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#13a688] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#356C58]" />
          </span>
        </span>

        {/* Expanded content */}
        {expanded && (
          <div className="flex items-center gap-3 pl-1">
            <span className="whitespace-nowrap text-[#FFFFFFB2]">
              Viewing as{" "}
              <span className="font-semibold text-white">{name}</span>
            </span>
            <button
              type="button"
              onClick={() => exitImpersonation(navigate)}
              className="shrink-0 rounded-lg !bg-[#356C58] px-3 py-1.5 text-xs font-semibold text-white transition hover:!bg-[#2f5f4d]"
            >
              Exit impersonation
            </button>
          </div>
        )}

        {/* Expand / collapse toggle */}
        <button
          type="button"
          onClick={toggleExpand}
          aria-label={expanded ? "Collapse" : "Expand"}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full  text-white transition "
        >
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
